import { Type } from '@angular/core';

import { CustomFilterTypeModel } from '../../../../Common/Filter/models/custom-filter-type.model';
import { AbstractFilterTypeComponent } from '../../../../Common/Filter/models/abstract-filter-type.component';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';
import { IncludeExcludeEnum } from '../../../../Common/Filter/enums/include-exclude.enum';
import { BasicNumberFilterModel } from '../../../../api/mentions/model/basic-number-filter.model';

/** A source an agent is live on, as the filter lists it - its name, then its type */
export interface AgentFilterSourceOption {
    id: number;
    label: string;
}

/** A past ad hoc run of an agent, labelled by what it ran over and when */
export interface AgentFilterAdHocOption {
    id: number;
    label: string;
}

/** One agent the filter offers, with everything it can be narrowed down to */
export interface AgentFilterOption {
    id: number;
    name: string;
    sources: AgentFilterSourceOption[];
    adHocRuns: AgentFilterAdHocOption[];
}

/**
 * What is ticked under one agent. `other` stands for whatever the agent saved from a source it no
 * longer has - deleted, or a legacy source the account can't see - which has nothing to list.
 */
export interface AgentFilterSelection {
    agentId: number;
    sourceIds: number[];
    other: boolean;
    adHocIds: number[];
}

/** How many of the source's mentions an agent saved, overall and by each of its live sources */
export interface AgentFacetCount {
    count: number;
    sourceCounts: Map<number, number>;
}

/** Which part of a selection a chip stands for, so removing the chip takes off just that part */
export enum AgentFilterChipPartEnum {
    agent = 'agent',
    liveSources = 'liveSources',
    source = 'source',
    other = 'other',
    adHocRuns = 'adHocRuns',
    adHocRun = 'adHocRun'
}

export interface AgentFilterChip extends ChipModel {
    agentId: number;
    part: AgentFilterChipPartEnum;
    /** The source or run, for the parts that are one */
    itemId?: number;
}

/** The query fields advanced monitoring attributes a mention to an agent by */
export interface AgentFilterQuery {
    aiAgent: BasicNumberFilterModel;
    aiAgentSource: BasicNumberFilterModel;
    aiAgentAdhoc: BasicNumberFilterModel;
}

/**
 * Filters mentions by the curation agents that saved them, and within an agent by the sources and ad
 * hoc runs it saved them from. Each agent is one chip; what is ticked under it only narrows it.
 */
export class AgentsFilterModel extends CustomFilterTypeModel<AgentFilterSelection[]> {

    /** Every agent on the account - what selections are resolved against, listed or not */
    options: AgentFilterOption[] = [];
    optionsLoaded: boolean = false;

    /** Mention counts by agent from the search's facets, or null where the search has none */
    counts: Map<number, AgentFacetCount> = null;

    /**
     * The agent the search is already scoped to - an agent source - or null. The filter then lists
     * that agent alone, to narrow it to some of its sources and ad hoc runs.
     */
    scopedAgentId: number = null;

    protected value: AgentFilterSelection[] = [];
    protected currentValue: AgentFilterSelection[] = [];

    chips: ChipModel[] = [];
    currentChips: ChipModel[] = [];

    /** A query read in before the agents arrived - it can only be resolved against them */
    private pendingQuery: AgentFilterQuery = null;

    constructor(
        id: string,
        label: string,
        component: Type<AbstractFilterTypeComponent>,
        section?: string,
        showIncludeExclude?: boolean
    ) {
        super(id, label, component, section, showIncludeExclude);
    }

    /** Every source, Other and every ad hoc run - what an agent starts with when it is ticked */
    static fullSelection(option: AgentFilterOption): AgentFilterSelection {
        return {
            agentId: option.id,
            sourceIds: option.sources.map(source => source.id),
            other: true,
            adHocIds: option.adHocRuns.map(run => run.id)
        };
    }

    /** Whether a query carries anything this filter would read back - an exclusion may name no agent */
    static hasQuery(query: AgentFilterQuery): boolean {
        return [query?.aiAgent, query?.aiAgentSource, query?.aiAgentAdhoc]
            .some(field => !!field?.include?.length || !!field?.exclude?.length);
    }

    static cloneSelections(selections: AgentFilterSelection[]): AgentFilterSelection[] {
        return (selections ?? []).map(selection => ({
            ...selection,
            sourceIds: selection.sourceIds.slice(),
            adHocIds: selection.adHocIds.slice()
        }));
    }

    /** Whether a query is waiting on the agents to be read in - see {@link setFromQuery} */
    get hasPendingQuery(): boolean {
        return !!this.pendingQuery;
    }

    setOptions(options: AgentFilterOption[]): void {
        this.options = options;
        this.optionsLoaded = true;

        if (this.pendingQuery) {
            const query = this.pendingQuery;
            this.pendingQuery = null;
            this.setFromQuery(query);
        } else {
            // Names may have changed, and chips set before the agents arrived had nothing to show
            this.chips = this.buildChips(this.value, this.includeExclude);
            this.currentChips = this.buildChips(this.currentValue, this.currentIncludeExclude);
        }
    }

    /**
     * The agents to list. With facet counts, as tags have, that is the agents with mentions in the
     * search, most first - plus any already ticked, so they can still be unticked.
     */
    get visibleOptions(): AgentFilterOption[] {
        if (this.scopedAgentId !== null) {
            return this.options.filter(option => option.id === this.scopedAgentId);
        }

        if (!this.counts) {
            return this.options;
        }

        return this.options
            .filter(option => this.countFor(option.id) > 0
                || this.currentValue.some(selection => selection.agentId === option.id))
            .sort((a, b) => this.countFor(b.id) - this.countFor(a.id));
    }

    /**
     * Other agents mean nothing on one agent's coverage, so scoping drops them - keeping that agent's
     * own selection, which may have been read in from the search before the scope was known
     */
    setScopedAgent(agentId: number): void {
        if (agentId === this.scopedAgentId) {
            return;
        }

        this.scopedAgentId = agentId;

        if (agentId !== null) {
            this.setValue(this.value.filter(selection => selection.agentId === agentId));
        }
    }

    setCounts(counts: Map<number, AgentFacetCount>): void {
        this.counts = counts;
    }

    /** Null without facet counts, so nothing is shown rather than a misleading zero */
    countFor(agentId: number): number {
        return this.counts ? this.counts.get(agentId)?.count ?? 0 : null;
    }

    sourceCountFor(agentId: number, sourceId: number): number {
        return this.counts ? this.counts.get(agentId)?.sourceCounts.get(sourceId) ?? 0 : null;
    }

    optionFor(agentId: number): AgentFilterOption {
        return this.options.find(option => option.id === agentId);
    }

    /**
     * Whether nothing under the agent has been unticked, so it is taken whole. Excluding has no Other
     * to tick - see {@link toQuery} - so there everything listed is enough.
     */
    isFullSelection(selection: AgentFilterSelection, exclude: boolean = !this.isInclude()): boolean {
        const option = this.optionFor(selection.agentId);

        if (!option) {
            return true;
        }

        return exclude
            ? option.sources.every(source => selection.sourceIds.includes(source.id)) && this.allAdHocRunsSelected(option, selection)
            : this.allLiveSourcesSelected(option, selection, false) && this.allAdHocRunsSelected(option, selection);
    }

    /** Every live source, and Other with them when including - the Live Sources group ticked as a whole */
    allLiveSourcesSelected(option: AgentFilterOption, selection: AgentFilterSelection, exclude: boolean = !this.isInclude()): boolean {
        if (!selection) {
            return false;
        }

        const sourcesSelected = option.sources.every(source => selection.sourceIds.includes(source.id));

        // Excluding lists no Other, so an agent without live sources has no group to tick
        return exclude ? option.sources.length > 0 && sourcesSelected : selection.other && sourcesSelected;
    }

    allAdHocRunsSelected(option: AgentFilterOption, selection: AgentFilterSelection): boolean {
        return !!selection && option.adHocRuns.every(run => selection.adHocIds.includes(run.id));
    }

    hasValue(): boolean {
        return this.value.length > 0;
    }

    setValue(value: AgentFilterSelection[]): void {
        this.value = value ?? [];
        this.chips = this.buildChips(this.value, this.includeExclude);
        this.cancelChanges();
    }

    getValue(): AgentFilterSelection[] {
        return this.value;
    }

    setCurrentValue(value: AgentFilterSelection[]): void {
        this.currentValue = value ?? [];
        this.currentChips = this.buildChips(this.currentValue, this.currentIncludeExclude);
    }

    getCurrentValue(): AgentFilterSelection[] {
        return this.currentValue;
    }

    removeChip(chip: ChipModel): void {
        this.setValue(this.withoutChip(this.value, chip as AgentFilterChip));
    }

    removeCurrentChip(chip: ChipModel): void {
        this.setCurrentValue(this.withoutChip(this.currentValue, chip as AgentFilterChip));
    }

    cancelChanges(): void {
        this.setCurrentValue(AgentsFilterModel.cloneSelections(this.value));
    }

    clear(): void {
        this.pendingQuery = null;
        this.setValue([]);
        this.setIncludeExclude(IncludeExcludeEnum.include);
    }

    /**
     * The query fields for the selection, as the other filters build theirs from the one being edited.
     *
     * Excluding takes an agent whole when everything under it is ticked, and otherwise just the sources
     * and ad hoc runs that are. Other can't be excluded on its own - its mentions carry source ids no
     * longer listed anywhere - so it is only excluded along with the whole agent.
     *
     * Including narrows an agent, and advanced monitoring has two ways to
     * say so: excluding what was unticked, or including what was ticked. Only exclusions can keep
     * Other, since its mentions carry source ids no longer listed anywhere, so they are used whenever
     * every narrowed agent still has Other ticked. Otherwise the ticked ids are included instead -
     * every agent's, as the include lists are shared and anything left off them drops out. That
     * loses Other for the agents that kept it, which the query has no way to express alongside.
     */
    toQuery(): AgentFilterQuery {
        const query: AgentFilterQuery = {
            aiAgent: { include: [], exclude: [] },
            aiAgentSource: { include: [], exclude: [] },
            aiAgentAdhoc: { include: [], exclude: [] }
        };
        const agentIds = this.currentValue.map(selection => selection.agentId);

        if (agentIds.length < 1) {
            return query;
        }

        if (!this.isInclude()) {
            // Scoped, the agent can't exclude itself - that would leave nothing - so only what was ticked goes
            this.currentValue.forEach(selection => {
                if (this.isFullSelection(selection, true) && this.scopedAgentId === null) {
                    query.aiAgent.exclude.push(selection.agentId);
                } else {
                    query.aiAgentSource.exclude.push(...selection.sourceIds);
                    query.aiAgentAdhoc.exclude.push(...selection.adHocIds);
                }
            });

            return query;
        }

        query.aiAgent.include = agentIds;

        const narrowed = this.currentValue.filter(selection => !this.isFullSelection(selection));

        if (narrowed.length < 1) {
            return query;
        }

        if (narrowed.every(selection => selection.other)) {
            narrowed.forEach(selection => {
                const option = this.optionFor(selection.agentId);

                query.aiAgentSource.exclude.push(...option.sources
                    .map(source => source.id)
                    .filter(id => !selection.sourceIds.includes(id)));
                query.aiAgentAdhoc.exclude.push(...option.adHocRuns
                    .map(run => run.id)
                    .filter(id => !selection.adHocIds.includes(id)));
            });
        } else {
            this.currentValue.forEach(selection => {
                query.aiAgentSource.include.push(...selection.sourceIds);
                query.aiAgentAdhoc.include.push(...selection.adHocIds);
            });
        }

        return query;
    }

    /** Reads a query written by {@link toQuery} back in, once the agents are known */
    setFromQuery(query: AgentFilterQuery): void {
        if (!this.optionsLoaded) {
            this.pendingQuery = query;

            return;
        }

        const { selections, includeExclude } = this.selectionsFromQuery(query);

        this.setIncludeExclude(includeExclude);
        this.setValue(selections);
    }

    /** Adds a saved filter's agents to the selection being edited */
    addCurrentFromQuery(query: AgentFilterQuery): void {
        const { selections } = this.selectionsFromQuery(query);
        const current = this.currentValue.filter(selection =>
            !selections.some(added => added.agentId === selection.agentId));

        this.setCurrentValue([...current, ...selections]);
    }

    /** The chips a query would show, for summing up a saved filter that isn't applied */
    chipsForQuery(query: AgentFilterQuery): { chips: ChipModel[]; includeExclude: IncludeExcludeEnum } {
        const { selections, includeExclude } = this.selectionsFromQuery(query);

        return { chips: this.buildChips(selections, includeExclude), includeExclude };
    }

    private selectionsFromQuery(query: AgentFilterQuery): { selections: AgentFilterSelection[]; includeExclude: IncludeExcludeEnum } {
        const agentInclude = (query?.aiAgent?.include ?? []).map(Number);
        const agentExclude = (query?.aiAgent?.exclude ?? []).map(Number);
        const sourceInclude = (query?.aiAgentSource?.include ?? []).map(Number);
        const sourceExclude = (query?.aiAgentSource?.exclude ?? []).map(Number);
        const adHocInclude = (query?.aiAgentAdhoc?.include ?? []).map(Number);
        const adHocExclude = (query?.aiAgentAdhoc?.exclude ?? []).map(Number);
        const narrowedByInclude = sourceInclude.length > 0 || adHocInclude.length > 0;

        // Including always names its agents. Excluding names the ones taken whole, and is left to find
        // the rest by the sources and runs that belong to them
        if (!agentInclude.length) {
            const excluded = this.options
                .map(option => {
                    if (agentExclude.includes(option.id)) {
                        return AgentsFilterModel.fullSelection(option);
                    }

                    const sourceIds = option.sources.map(source => source.id).filter(id => sourceExclude.includes(id));
                    const adHocIds = option.adHocRuns.map(run => run.id).filter(id => adHocExclude.includes(id));

                    return sourceIds.length || adHocIds.length
                        ? { agentId: option.id, sourceIds, other: false, adHocIds }
                        : null;
                })
                .filter(selection => !!selection);

            return { selections: excluded, includeExclude: IncludeExcludeEnum.exclude };
        }

        // An agent that has since been deleted has nothing left to show, so it is dropped
        const selections = agentInclude
            .map(agentId => this.optionFor(agentId))
            .filter(option => !!option)
            .map(option => {
                const selection = AgentsFilterModel.fullSelection(option);

                if (narrowedByInclude) {
                    return {
                        ...selection,
                        sourceIds: selection.sourceIds.filter(id => sourceInclude.includes(id)),
                        other: false,
                        adHocIds: selection.adHocIds.filter(id => adHocInclude.includes(id))
                    };
                }

                return {
                    ...selection,
                    sourceIds: selection.sourceIds.filter(id => !sourceExclude.includes(id)),
                    adHocIds: selection.adHocIds.filter(id => !adHocExclude.includes(id))
                };
            });

        return { selections, includeExclude: IncludeExcludeEnum.include };
    }

    /** Takes the part a chip stands for out of the selections, dropping an agent left with nothing */
    private withoutChip(selections: AgentFilterSelection[], chip: AgentFilterChip): AgentFilterSelection[] {
        return AgentsFilterModel.cloneSelections(selections)
            .map(selection => {
                if (selection.agentId !== chip.agentId) {
                    return selection;
                }

                switch (chip.part) {
                    case AgentFilterChipPartEnum.agent:
                        return null;
                    case AgentFilterChipPartEnum.liveSources:
                        selection.sourceIds = [];
                        selection.other = false;
                        break;
                    case AgentFilterChipPartEnum.source:
                        selection.sourceIds = selection.sourceIds.filter(id => id !== chip.itemId);
                        break;
                    case AgentFilterChipPartEnum.other:
                        selection.other = false;
                        break;
                    case AgentFilterChipPartEnum.adHocRuns:
                        selection.adHocIds = [];
                        break;
                    case AgentFilterChipPartEnum.adHocRun:
                        selection.adHocIds = selection.adHocIds.filter(id => id !== chip.itemId);
                        break;
                }

                return selection.other || selection.sourceIds.length || selection.adHocIds.length ? selection : null;
            })
            .filter(selection => !!selection);
    }

    /**
     * One chip for an agent ticked whole. Otherwise one for each thing ticked under it, with a group
     * ticked as a whole - Live Sources, or Ad Hoc Events - standing in for everything in it.
     */
    private buildChips(selections: AgentFilterSelection[], includeExclude: IncludeExcludeEnum): AgentFilterChip[] {
        const exclude = includeExclude === IncludeExcludeEnum.exclude;
        const suffix = exclude ? 'Common.forms.chips.exclude.suffix' : '';

        return selections.flatMap(selection => {
            const option = this.optionFor(selection.agentId);

            if (!option) {
                return [];
            }

            const chip = (part: AgentFilterChipPartEnum, label: string, item?: string, itemId?: number): AgentFilterChip => ({
                label,
                labelArgs: { agent: option.name, item },
                suffix,
                agentId: option.id,
                part,
                itemId
            });

            if (this.isFullSelection(selection, exclude)) {
                return [chip(AgentFilterChipPartEnum.agent, 'Monitoring.filtersLocalization.aiAgents.chip.agent')];
            }

            const chips: AgentFilterChip[] = [];

            if (this.allLiveSourcesSelected(option, selection, exclude)) {
                chips.push(chip(AgentFilterChipPartEnum.liveSources, 'Monitoring.filtersLocalization.aiAgents.chip.liveSources'));
            } else {
                option.sources
                    .filter(source => selection.sourceIds.includes(source.id))
                    .forEach(source => chips.push(
                        chip(AgentFilterChipPartEnum.source, 'Monitoring.filtersLocalization.aiAgents.chip.item', source.label, source.id)
                    ));

                if (selection.other && !exclude) {
                    chips.push(chip(AgentFilterChipPartEnum.other, 'Monitoring.filtersLocalization.aiAgents.chip.other'));
                }
            }

            if (option.adHocRuns.length > 0 && this.allAdHocRunsSelected(option, selection)) {
                chips.push(chip(AgentFilterChipPartEnum.adHocRuns, 'Monitoring.filtersLocalization.aiAgents.chip.adHocRuns'));
            } else {
                option.adHocRuns
                    .filter(run => selection.adHocIds.includes(run.id))
                    .forEach(run => chips.push(
                        chip(AgentFilterChipPartEnum.adHocRun, 'Monitoring.filtersLocalization.aiAgents.chip.item', run.label, run.id)
                    ));
            }

            return chips;
        });
    }
}
